import { forwardRef } from 'react';

import { IconX } from '@tabler/icons-react';
import { TooltipRenderProps } from 'react-joyride';
import { Button } from 'ui-kit';

import { TOUR_CONFIGS } from './config';
import { useTourContext } from './TourProvider';

export const TourTooltip = forwardRef<HTMLDivElement, TooltipRenderProps>((props, ref) => {
  const { continuous, index, step, backProps, primaryProps, skipProps, tooltipProps } = props;

  const { currentTourId, steps, dismissTourPermanently, stopTour } = useTourContext();
  const displayBehavior = currentTourId ? TOUR_CONFIGS[currentTourId]?.displayBehavior : undefined;

  const isFirstStep = index === 0;
  const isLastStep = continuous && steps.length > 0 && index === steps.length - 1;
  const showDontShowAgain = displayBehavior && displayBehavior !== 'once';

  const { ref: _, ...restTooltipProps } = tooltipProps;

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    stopTour();
  };

  const handleDontShowAgain = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (currentTourId) {
      await dismissTourPermanently(currentTourId);
    }
    stopTour();
  };

  return (
    <div
      ref={ref}
      {...restTooltipProps}
      className="relative rounded-lg border border-gray-700 bg-gray-950 p-5 text-white shadow-lg light:border-gray-300 light:bg-white light:text-gray-900"
      style={{
        maxWidth: '320px',
        zIndex: 9999,
      }}
    >
      <button
        type="button"
        onClick={handleClose}
        className="absolute right-[2px] top-[2px] flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-200 light:text-gray-600 light:hover:text-gray-900"
        aria-label="Close"
      >
        <IconX size={18} />
      </button>
      {step.title && (
        <h4 className="mb-2 pr-6 text-base font-semibold text-white light:text-gray-900">
          {step.title}
        </h4>
      )}
      <div className="mb-4 text-sm text-gray-300 light:text-gray-700">{step.content}</div>
      <div className="flex items-center justify-end gap-2">
        {index > 0 && (
          <Button label={backProps.title || 'Back'} size="small" type="secondary" {...backProps}>
            {backProps.title}
          </Button>
        )}
        {continuous && !isLastStep && (
          <>
            {isFirstStep && showDontShowAgain && (
              <Button
                label="Don't show me this again"
                size="small"
                type="secondary"
                onClick={handleDontShowAgain}
              >
                Don't show me this again
              </Button>
            )}
            <Button
              label={primaryProps.title || 'Next'}
              size="small"
              type={isFirstStep ? 'primary_light' : 'primary'}
              {...primaryProps}
            >
              {primaryProps.title}
            </Button>
          </>
        )}
        {continuous && isLastStep && showDontShowAgain && (
          <Button
            label="Don't show me this again"
            size="small"
            type="primary"
            onClick={handleDontShowAgain}
          >
            Don't show me this again
          </Button>
        )}
        {continuous && isLastStep && !showDontShowAgain && (
          <Button
            label={primaryProps.title || 'Next'}
            size="small"
            type="primary"
            {...primaryProps}
          >
            {primaryProps.title}
          </Button>
        )}
        {!continuous && showDontShowAgain && (
          <Button
            label="Don't show me this again"
            size="small"
            type="primary"
            onClick={handleDontShowAgain}
          >
            Don't show me this again
          </Button>
        )}
        {!continuous && !showDontShowAgain && (
          <Button label="Skip" size="small" type="primary" {...skipProps}>
            Skip
          </Button>
        )}
      </div>
    </div>
  );
});

TourTooltip.displayName = 'TourTooltip';
